import { useOperatorDistanceUnit, useUsersCurrentAccessesQuery } from '@/api/queries/users';
import Search from '@mui/icons-material/Search';
import { Button, Grid, InputAdornment, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useOperatorsRidesFilterQuery } from 'api/queries/operators';
import { PaginationQueryParams } from 'api/types';
import { OperatorRideDtoSchemaType } from 'components/forms/BookingFormFactory/types';
import ChangeRideEndModal from 'components/modals/ChangeRideEndModal';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  colors,
  ControlledField,
  DataTable,
  DistanceUnit,
  formatDateTimeLongWithLocale,
  UserPermission,
} from 'remote/global';
import { ROUTES } from 'utils/data/routes';
import useDebounce from 'utils/hooks/useDebounce';
import { v4 as uuidv4 } from 'uuid';
import { convertDistanceUnit } from 'utils/helpers';
import {
  ModalContextType,
  WrappedOrderDetailsModal,
} from '@/components/modals/OrderModal/WrappedOrderDetailsModal';

export function ShowOrderDetailsModalLink({
  bookingNumber,
  rideId,
  text,
}: {
  bookingNumber: string;
  rideId: number;
  text: string;
}) {
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);

  return (
    <>
      <Button
        variant='text'
        onClick={() => setIsOrderDetailsModalOpen(true)}
        size='small'
        sx={{ fontSize: '14px', p: 0, height: 'auto' }}
      >
        {text}
      </Button>
      {isOrderDetailsModalOpen && (
        <WrappedOrderDetailsModal
          open={isOrderDetailsModalOpen}
          setOpen={setIsOrderDetailsModalOpen}
          onClose={() => setIsOrderDetailsModalOpen(false)}
          bookingNumber={bookingNumber}
          rideId={rideId}
          contextType={ModalContextType.RIDE_VIEW}
        />
      )}
    </>
  );
}

enum TabItem {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  UPCOMING = 'UPCOMING',
}

const tabs = [TabItem.ALL, TabItem.ACTIVE, TabItem.UPCOMING, TabItem.FINISHED];

function LogisticsAllRidesView() {
  const [activeTab, setActiveTab] = useState<TabItem>(TabItem.ALL);
  const [isRideEndModalOpen, setIsRideEndModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<OperatorRideDtoSchemaType | null>(null);
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const { control, watch, setValue } = useForm<PaginationQueryParams>({
    defaultValues: {
      search: '',
      pageNo: '0',
    },
  });
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo || {};
  const distanceUnit = useOperatorDistanceUnit();
  const [search, pageNo] = watch(['search', 'pageNo']);
  const debouncedSearch = useDebounce(search, 300);
  const { data: allRides, isLoading } = useOperatorsRidesFilterQuery({
    search: debouncedSearch,
    onlyCompleted: activeTab === TabItem.FINISHED,
    onlyUpcoming: activeTab === TabItem.UPCOMING,
    onlyActive: activeTab === TabItem.ACTIVE,
    pageNo,
  });

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'bookingNumber',
        headerName: `${t('LogisticsView.rides.table.header.bookingNumber')}`,
        flex: 1,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <div>
            {[UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
              permissions?.order as UserPermission
            ) ? (
              <Typography sx={{ color: colors.gray9, fontSize: '14px' }}>
                #{row.bookingNumber}
              </Typography>
            ) : (
              <ShowOrderDetailsModalLink
                bookingNumber={row.bookingNumber}
                rideId={row.rideId}
                text={`#${row.bookingNumber} (${row.rideNumber})`}
              />
            )}
          </div>
        ),
      },
      {
        field: 'estimatedStartAtLocal',
        headerName: `${t('LogisticsView.rides.table.header.startedAtDate')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
      },
      {
        field: 'estimatedEndAtLocal',
        headerName: `${t('LogisticsView.rides.table.header.estimatedEndAt')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
      },
      {
        field: 'clientStartAddress',
        headerName: `${t('LogisticsView.rides.table.header.clientStartAddress')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'clientEndAddress',
        headerName: `${t('LogisticsView.rides.table.header.clientEndAddress')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'passengerCount',
        headerName: `${t('LogisticsView.rides.table.header.passengerCount')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'seats',
        headerName: `${t('LogisticsView.rides.table.header.seats')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'licensePlate',
        headerName: `${t('LogisticsView.rides.table.header.licensePlate')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography
            sx={{ fontWeight: 400, fontSize: '14px', color: colors.headerBlack }}
            component={Link}
            to={[ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET, row.vehicleId].join('/')}
          >
            {row.licensePlate}
          </Typography>
        ),
      },
      {
        field: 'driverName',
        headerName: `${t('LogisticsView.rides.table.header.driverName')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography
            sx={{ fontWeight: 400, fontSize: '14px', color: colors.headerBlack }}
            component={Link}
            to={[ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DRIVERS, row.driverId].join('/')}
          >
            {row.driverName}
          </Typography>
        ),
      },
      {
        field: 'mileageKm',
        headerName: `${t('LogisticsView.rides.table.header.estMileageKm')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => convertDistanceUnit(row.mileageKm, DistanceUnit.KM, distanceUnit),
      },
      {
        field: 'hours',
        headerName: `${t('LogisticsView.rides.table.header.estHours')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
      },
    ];
  }, [t, permissions?.order, i18n.language, distanceUnit]);

  const handleClearRideEndModalState = () => {
    setIsRideEndModalOpen(false);
    setSelectedRow(null);
  };

  return (
    <>
      <ChangeRideEndModal
        open={isRideEndModalOpen}
        setOpen={() => setIsRideEndModalOpen(false)}
        onClose={handleClearRideEndModalState}
        defaultValues={{
          newFinishTime: selectedRow?.estimatedEndAt,
          rideId: selectedRow?.rideId,
        }}
      />

      <Grid container spacing={1}>
        <Grid item>
          <ControlledField
            control={control}
            name='search'
            fieldProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Tabs
            sx={{ pb: '1rem' }}
            value={activeTab}
            onChange={(_, tab) => setActiveTab(tab)}
            variant='scrollable'
          >
            {tabs.map((tab) => (
              <Tab key={tab} label={t(`terms.attributes.ride.types.${tab}`)} value={tab} />
            ))}
          </Tabs>
        </Grid>
        <Grid item sx={{ width: '100%' }}>
          <DataTable
            rows={allRides ?? []}
            columns={columns}
            loading={isLoading}
            getRowId={() => uuidv4()}
            onPageChange={(page) => setValue('pageNo', String(page))}
            t={t}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default LogisticsAllRidesView;
